import { useEffect, useState } from "react";
import Label from "../../form/Label";
import Input from "../../form/input/InputField";
import Button from "../button/Button";
import { Modal } from ".";
import Alert from "../alert/Alert";
import { request } from "../../../utils";
import { useNavigate } from "react-router";

interface VerifyAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
  email: string;
}

export default function VerifyAccountModal({
  isOpen,
  onClose,
  email,
}: VerifyAccountModalProps) {
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [timer, setTimer] = useState(5);
  const navigate = useNavigate();

  const closeModal = () => {
    onClose();
    setOtp("");
    setError("");
    setSuccess("");
    setTimer(5);
    navigate("/")
  };

  // ---- Countdown timer
  useEffect(() => {
    if (!isOpen || timer <= 0) return;
  
    const interval = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  
    return () => clearInterval(interval);
  }, [isOpen, timer]);  

  // ---- Verify account using email passed as prop and otp
  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");

    try {
      await request.post({
        url: "/user/verify-email",
        data: { email, otp },
      });

      setSuccess("Account verified successfully. You can now log in.");
      
      setTimeout(() => {
        navigate("/");
      }, 3000);
    } catch (err) {
      setError((err as any).message || "Failed to verify account.");

      setTimeout(() => {
        setError("");
      }, 3000)
    } finally {
      setLoading(false);
    }
  };

  // ---- Resend OTP to the email
  const handleResend = async () => {
    try {
      setResending(true);
      setError("");

      await request.post({
        url: "/user/otp/email",
        data: { email },
        auth: false
      });
      
      setTimer(120);
    } catch (err) {
      setError((err as any).message || "Failed to resend OTP.");
      setTimeout(() => setError(""), 3000);
    } finally {
      setResending(false);
    }
  };

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60)
      .toString()
      .padStart(2, "0");
    const s = (seconds % 60).toString().padStart(2, "0");
    return `${m}:${s}`;
  };

  return (
    <Modal isOpen={isOpen} onClose={closeModal} className="max-w-md p-6">
      <form onSubmit={handleVerify}>
        <div className="space-y-6">
          <h2 className="text-xl font-semibold text-gray-800 dark:text-white">
            Verify Your Account
          </h2>

          {error && <Alert variant="error" title="Error" message={error} />}
          {success && (
            <Alert variant="success" title="Success" message={success} />
          )}

          <div>
            <Label>
              OTP <span className="text-error-500">*</span>
            </Label>
            <Input
              type="text"
              placeholder="Enter 4-digit OTP"
              maxLength={4}
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              required
            />
          </div>

          <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-300">
            <span>
              OTP expires in: <strong>{formatTime(timer)}</strong>
            </span>
            <button
              type="button"
              disabled={resending || timer > 0}
              className="text-primary-500 disabled:opacity-50"
              onClick={handleResend}
            >
              {resending ? "Resending..." : "Resend OTP"}
            </button>
          </div>

          <div>
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? "Verifying..." : "Verify"}
            </Button>
          </div>
        </div>
      </form>
    </Modal>
  );
}
